import { Component, ElementRef, EventEmitter, Input, OnChanges, Output } from "@angular/core";
import { DayOfWeek } from "@enums/DayOfWeek";
import { TranslationService } from "@services/translation.service";
import "./day-of-week-picker.component.scss";

interface IDayOfWeek {
    dayOfWeek: DayOfWeek;
    firstLetter: string;
    translation: string;
    selected?: boolean;
    highlighted?: boolean;
    firstInRange?: boolean;
    endInRange?: boolean;
}

@Component({
    selector: "day-of-week-picker",
    template: require("./day-of-week-picker.component.html")
})
export class DayOfWeekPickerComponent implements OnChanges {
    @Input() selectedDays: DayOfWeek[];
    @Input() range: boolean;

    @Output() selectedDaysChange = new EventEmitter<DayOfWeek[]>();

    // ---

    dayOfWeek = DayOfWeek;
    dayOfWeekMap: IDayOfWeek[];

    locale: string; /*= "es-SP"*/

    constructor(
        private readonly translationService: TranslationService
    ) {
        this.createDayOfWeekMap();
    }

    ngOnChanges() {
        this.setEnabledDays()
    }

    toggleDay(day: IDayOfWeek) {
        this.removeHighlights();

        if (this.range) {
            switch (this.selectedDays.length) {
                case 0:
                    this.selectSingleDay(day);
                    break;
                case 1:
                    this.setRange(day);
                    break;
                default:
                    this.selectSingleDay(day, true);
                    break;
            }
            return;
        }

        day.selected = !day.selected;

        this.onSelectionChanged();
    }

    selectDays(selection: "all" | "weekdays" | "weekends") {
        this.resetSelection();

        switch (selection) {
            case "weekdays":
                this.enableWeekdays();
                break;

            case "weekends":
                this.enableWeekends();
                break;

            default:
                this.enableWeekdays();
                this.enableWeekends();
        }

        this.onSelectionChanged();
    }

    highlightDays($event): void {
        if (!this.range || this.selectedDays.length !== 1) {
            return;
        }

        this.highlightRange($event);
    }

    removeHighlights(): void {
        this.dayOfWeekMap.forEach(day => day.highlighted = false);
    }

    resetRange(day: IDayOfWeek): void {
        this.resetSelection();
        this.selectedDays = [];
        this.onSelectionChanged();
    }

    // ---

    private setEnabledDays(): void {
        this.dayOfWeekMap.forEach(day => day.selected = false);
        this.selectedDays.forEach(day => {
            this.enableDay(day);
        });
    }

    private highlightRange(day: IDayOfWeek): void {
        this.removeHighlights();

        if (day.dayOfWeek == this.selectedDays[0]) {
            return;
        }

        if (day.dayOfWeek > this.selectedDays[0]) {

            this.dayOfWeekMap.forEach(dow => {
                if (dow.dayOfWeek < day.dayOfWeek && dow.dayOfWeek > this.selectedDays[0]) {
                    dow.highlighted = true;
                }
            })

            return;
        }

        this.dayOfWeekMap.forEach(dow => {
            if (dow.dayOfWeek < day.dayOfWeek || dow.dayOfWeek > this.selectedDays[0]) {
                dow.highlighted = true;
            }
        })

    }

    private setRange(clickedDay: IDayOfWeek) {
        if (clickedDay.dayOfWeek === this.selectedDays[0]) {
            return;
        }

        this.dayOfWeekMap.find(day => day.dayOfWeek === this.selectedDays[0]).firstInRange = true;
        clickedDay.endInRange = true;

        this.dayOfWeekMap.forEach((dow) => {
            if (clickedDay.dayOfWeek > this.selectedDays[0]) {
                dow.selected = dow.dayOfWeek <= clickedDay.dayOfWeek && dow.dayOfWeek >= this.selectedDays[0];
                return;
            }
            dow.selected = dow.dayOfWeek <= clickedDay.dayOfWeek || dow.dayOfWeek >= this.selectedDays[0];
        });

        this.onSelectionChanged();
    }

    private selectSingleDay(day: IDayOfWeek, resetSelection: boolean = false): void {
        resetSelection && this.resetSelection();
        this.selectedDays = [];

        day.selected = true;
        this.onSelectionChanged();
    }

    private createDayOfWeekMap() {
        this.dayOfWeekMap = [];

        const iterations = 7;

        for (var i = 0; i < iterations; i++) {
            const dayIndex = i < 7 ? i : i - 7;

            var translation = this.getTranslatedDayOfWeek(dayIndex);

            var dayOfWeek: IDayOfWeek = {
                dayOfWeek: dayIndex,
                firstLetter: translation[0],
                translation: translation
            };

            this.dayOfWeekMap.push(dayOfWeek);
        }

        // If Monday is first, then shift sunday to back of array (note this logic needs to be added to preference)
        var sunday = this.dayOfWeekMap.shift();
        this.dayOfWeekMap.push(sunday);
    }

    private getTranslatedDayOfWeek(dayOfWeek: DayOfWeek): string {

        var today = new Date();
        while (today.getDay() != dayOfWeek) {
            today.setDate(today.getDate() - 1);
        }


        const locale = localStorage.getItem('userSelectedLanguageCode');

        return today.toLocaleDateString(locale, { weekday: "long" });
    }

    private enableWeekdays() {
        this.enableDay(DayOfWeek.Monday);
        this.enableDay(DayOfWeek.Tuesday);
        this.enableDay(DayOfWeek.Wednesday);
        this.enableDay(DayOfWeek.Thursday);
        this.enableDay(DayOfWeek.Friday);
    }

    private enableWeekends() {
        this.enableDay(DayOfWeek.Saturday);
        this.enableDay(DayOfWeek.Sunday);
    }

    private enableDay(dayOfWeek: DayOfWeek) {
        var day = this.dayOfWeekMap.find(d => d.dayOfWeek == dayOfWeek);
        day.selected = true;
    }

    private onSelectionChanged() {
        let selectedDays: DayOfWeek[] = [];

        if (this.range && this.selectedDays.length != 0) {
            selectedDays = this.getSelectedDaysRange();
            this.selectedDaysChange.emit(selectedDays);
            return;
        }

        this.dayOfWeekMap.forEach((d, i) => {
            if (d.selected) {
                selectedDays.push(d.dayOfWeek);
            }
        });

        this.selectedDaysChange.emit(selectedDays);
    }

    private getSelectedDaysRange(): number[] {
        // Ensure range is returned in the correct order i.e. [Sat, Sun, Mon] instead of [Mon, Sat, Sun]

        let selectedRange: number[] = [];

        const firstDayInRange = this.dayOfWeekMap.find(day => day.firstInRange);

        if (!firstDayInRange) {
            return;
        }

        selectedRange.push(firstDayInRange.dayOfWeek);

        for (let i = firstDayInRange.dayOfWeek; i < this.dayOfWeekMap.length; i++) {
            if (this.dayOfWeekMap[i].selected) {

                if (this.dayOfWeekMap[i].dayOfWeek == 0 && selectedRange.includes(0)) {
                    // Skip if Sunday already exists in array otherwise it will be added twice due to having a dayOfWeek of 0 and being at the end of the dayOfWeekMap array
                    continue;
                }

                selectedRange.push(this.dayOfWeekMap[i].dayOfWeek);
            }
        }

        if (firstDayInRange.dayOfWeek != 0) {
            for (let i = 0; i < firstDayInRange.dayOfWeek - 1; i++) {
                if (this.dayOfWeekMap[i].selected) {
                    selectedRange.push(this.dayOfWeekMap[i].dayOfWeek);
                }
            }

        }

        return selectedRange;
    }

    private resetSelection() {
        this.dayOfWeekMap.forEach(d => {
            d.selected = false;
            d.firstInRange = false;
            d.endInRange = false;
        });

        this.onSelectionChanged();
    }

}
